import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CopyOutlined,
  CreditCardOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import apiClient from "../../api/apiClient";
import { useSelection } from "../../SelectionContext";
import { useEcommercePermissions } from "../../hooks/useEcommercePermissions";
import useBusinessResource from "../../hooks/useBusinessResource";
import API_CONFIG from "../../config/api.js";

const { Title, Text, Paragraph } = Typography;
const API_URL = API_CONFIG.BASE_URL;

interface YengaPayConfigCardProps {
  storeId: number | null;
  isMobile?: boolean;
  active?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  onBusyChange?: (busy: boolean) => void;
}

interface PaymentModeOption {
  id: number;
  name: string;
  mode_type: string;
  company_id: number | null;
  integration_code?: string | null;
}

interface TreasuryAccountOption {
  id: number;
  name: string;
  account_type: string;
  currency_code?: string | null;
  is_default: number | boolean;
}

interface FinancialRoute {
  id: number;
  payment_mode_id: number;
  payment_mode_name: string;
  payment_mode_type: string;
  treasury_account_id: number;
  treasury_account_name: string;
  treasury_account_type: string;
  treasury_account_currency_code?: string | null;
  is_active: boolean;
}

interface RoutingIssue {
  code: string;
  message: string;
}

interface GatewayConfig {
  is_enabled: boolean;
  api_env: "test" | "prod";
  group_id: string | null;
  project_id: string | null;
  api_key_masked: string | null;
  webhook_secret_masked: string | null;
  configured: boolean;
  webhook_configured: boolean;
  webhook_url: string;
  server_ready: boolean;
  financial_ready: boolean;
  checkout_ready: boolean;
  routing: FinancialRoute | null;
  routing_issues: RoutingIssue[];
  options: {
    payment_modes: PaymentModeOption[];
    treasury_accounts: TreasuryAccountOption[];
  };
}

const accountTypeLabels: Record<string, string> = {
  cash: "Caisse",
  bank: "Banque",
  mobile: "Mobile Money",
};

/**
 * Configuration technique et financière de YengaPay pour une boutique.
 * Le mode de paiement est relié par ID au compte de trésorerie choisi : le
 * libellé saisi ailleurs dans l'application n'intervient jamais dans le routage.
 */
const YengaPayConfigCard: React.FC<YengaPayConfigCardProps> = ({ storeId, isMobile, active = true, onDirtyChange, onBusyChange }) => {
  const [form] = Form.useForm();
  const [modal, modalContext] = Modal.useModal();
  const [notices, noticeContext] = message.useMessage();
  const enabled = Form.useWatch("is_enabled", form);
  const { selectedCompany, selectedWarehouse } = useSelection();
  const { user, canViewStores, canEditStores } = useEcommercePermissions();
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const [conflict, setConflict] = useState(false);
  const [config, setConfig] = useState<GatewayConfig | null>(null);
  const mounted = useRef(true);
  const abort = useRef<AbortController | null>(null);
  const headers = { "X-Company-Id": String(selectedCompany || "") };
  const resource = useBusinessResource<GatewayConfig>({
    resource: "storefront-payment", userId: user?.id, companyId: selectedCompany, warehouseId: selectedWarehouse,
    moduleScope: "ecommerce", parameters: [storeId], enabled: Boolean(storeId && canViewStores()), active,
    load: async (signal) => {
      const { data } = await apiClient.get(`${API_URL}/api/ecommerce/payment-gateways/${storeId}`, { signal, headers });
      return data;
    },
  });
  const hydrate = useCallback((gateway: GatewayConfig) => {
    setConfig(gateway); setDirty(false); dirtyRef.current = false; setConflict(false);
    form.setFieldsValue({ is_enabled: gateway.is_enabled, api_env: gateway.api_env || "test",
      group_id: gateway.group_id || "", project_id: gateway.project_id || "", api_key: "", webhook_secret: "",
      payment_mode_id: gateway.routing?.payment_mode_id, treasury_account_id: gateway.routing?.treasury_account_id });
  }, [form]);
  useEffect(() => {
    if (!resource.data || savingRef.current) return;
    if (!dirtyRef.current) hydrate(resource.data);
    else if (JSON.stringify(config) !== JSON.stringify(resource.data)) setConflict(true);
  }, [resource.data, config, hydrate]);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => { onBusyChange?.(saving); }, [saving, onBusyChange]);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; abort.current?.abort(); };
  }, []);
  const reload = () => {
    const run = () => resource.refresh().then((gateway) => { if (mounted.current) hydrate(gateway); })
      .catch(() => notices.error("Impossible de recharger le paiement. Votre saisie est conservée."));
    if (dirty) modal.confirm({ title: "Recharger la configuration de paiement ?", content: "Les modifications et les clés non enregistrées seront abandonnées.",
      okText: "Recharger", cancelText: "Conserver ma saisie", onOk: run });
    else void run();
  };
  const handleSave = async () => {
    if (!storeId || !canEditStores() || !config || !config.server_ready || conflict || savingRef.current || resource.error) return;
    let values: any;
    try { values = await form.validateFields(); } catch { return; }
    savingRef.current = true; setSaving(true);
    const controller = new AbortController(); abort.current = controller;
    try {
      const latest = await resource.refresh();
      if (!mounted.current || controller.signal.aborted) return;
      if (JSON.stringify(config) !== JSON.stringify(latest)) { setConflict(true); return; }
      if (values.is_enabled && !latest.options.treasury_accounts.some((account) => account.id === values.treasury_account_id)) {
        setConflict(true); return;
      }
      if (values.payment_mode_id && !latest.options.payment_modes.some((mode) => mode.id === values.payment_mode_id)) {
        setConflict(true); return;
      }
      const payload: Record<string, unknown> = { is_enabled: Boolean(values.is_enabled), api_env: values.api_env || "test",
        group_id: (values.group_id || "").trim() || null, project_id: (values.project_id || "").trim() || null,
        payment_mode_id: values.payment_mode_id || null, treasury_account_id: values.treasury_account_id || null };
      if (values.api_key?.trim()) payload.api_key = values.api_key.trim();
      if (values.webhook_secret?.trim()) payload.webhook_secret = values.webhook_secret.trim();
      await apiClient.put(`${API_URL}/api/ecommerce/payment-gateways/${storeId}`, payload, { headers, signal: controller.signal });
      if (!mounted.current || controller.signal.aborted) return;
      const saved = await resource.refresh();
      if (mounted.current) { hydrate(saved); notices.success("Configuration YengaPay et routage financier enregistrés."); }
    } catch (error: any) {
      if (mounted.current && !controller.signal.aborted) notices.error(error?.response?.data?.error || "Impossible d'enregistrer ou de relire la configuration de paiement. Votre saisie est conservée.");
    } finally { if (mounted.current) { savingRef.current = false; setSaving(false); } }
  };

  const copyWebhookUrl = () => {
    const url = config?.webhook_url;
    if (!url) return;
    navigator.clipboard
      .writeText(url)
      .then(() => notices.success("URL du webhook copiée."))
      .catch(() =>
        notices.warning("Copie impossible, sélectionnez l'URL manuellement.")
      );
  };

  if (!storeId) return null;

  return (
    <Card
      className="ecommerce-admin-panel"
      bordered={false}
      style={{ marginTop: 16 }}
      aria-busy={resource.loading}
    >
      {modalContext}{noticeContext}
      {resource.error && <Alert type="error" showIcon message="La configuration de paiement n'a pas pu être actualisée."
        description="Les valeurs déjà chargées et votre saisie sont conservées." action={<Button onClick={() => void resource.refresh().catch(() => {})}>Réessayer</Button>} />}
      {conflict && <Alert type="warning" showIcon message="La configuration ou les comptes disponibles ont changé."
        description="Votre saisie est conservée. Rechargez la configuration avant d'enregistrer." action={<Button onClick={reload}>Recharger</Button>} />}
      <div className="ecommerce-admin-panel-header">
        <div>
          <Title level={4} className="ecommerce-admin-panel-title">
            <CreditCardOutlined /> Paiement en ligne (YengaPay)
          </Title>
          <Text className="ecommerce-admin-panel-description">
            Connectez la passerelle puis indiquez le compte commercial sur
            lequel chaque règlement doit être comptabilisé.
          </Text>
        </div>
      </div>

      {config?.checkout_ready && (
        <Alert
          className="ecommerce-admin-inline-alert"
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          message="YengaPay est prêt pour le checkout"
          description={`Les paiements seront enregistrés avec « ${
            config.routing?.payment_mode_name
          } » sur le compte « ${config.routing?.treasury_account_name} ».`}
        />
      )}

      {config?.is_enabled && !config.checkout_ready && (
        <Alert
          className="ecommerce-admin-inline-alert"
          type="warning"
          showIcon
          message="YengaPay n'est pas encore éligible au checkout"
          description={
            config.routing_issues?.[0]?.message ||
            (!config.webhook_configured
              ? "Le Webhook Secret doit être renseigné."
              : "Complétez la configuration technique et financière.")
          }
        />
      )}

      {config && !config.server_ready && (
        <Alert
          className="ecommerce-admin-inline-alert"
          type="warning"
          showIcon
          message="Chiffrement non configuré côté serveur"
          description="La clé PAYMENT_ENCRYPTION_KEY est absente. Contactez l'administrateur."
        />
      )}

      <Alert
        className="ecommerce-admin-inline-alert"
        type="info"
        showIcon
        message="Connexion et routage sont deux validations distinctes"
        description="Vous n'avez pas à créer un mode portant exactement le même nom. Si aucun mode n'est sélectionné, ELSA crée le mode canonique YengaPay. Le routage ne devient actif qu'après votre sélection explicite du compte de trésorerie destinataire."
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical" disabled={!canEditStores() || saving || !config || Boolean(resource.error)}
        onValuesChange={() => { dirtyRef.current = true; setDirty(true); }}>
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Activer le paiement en ligne"
              name="is_enabled"
              valuePropName="checked"
              tooltip="L'option n'apparaît au client que lorsque toute la configuration est prête."
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Environnement" name="api_env">
              <Select
                options={[
                  { value: "test", label: "Test (sandbox)" },
                  { value: "prod", label: "Production" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Group ID" name="group_id">
              <Input placeholder="YengaPay Group ID" autoComplete="off" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Project ID (Payment Intent)" name="project_id">
              <Input placeholder="YengaPay Project ID" autoComplete="off" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="API Key"
              name="api_key"
              tooltip="Laissez vide pour conserver la clé existante."
              extra={
                config?.api_key_masked
                  ? `Actuelle : ${config.api_key_masked}`
                  : "Aucune clé enregistrée"
              }
            >
              <Input.Password
                placeholder={
                  config?.api_key_masked
                    ? "•••••••• (inchangée)"
                    : "Saisir l'API Key"
                }
                autoComplete="new-password"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Webhook Secret"
              name="webhook_secret"
              tooltip="Obligatoire pour authentifier les confirmations de paiement."
              extra={
                config?.webhook_secret_masked
                  ? `Actuel : ${config.webhook_secret_masked}`
                  : "Aucun secret enregistré"
              }
            >
              <Input.Password
                placeholder={
                  config?.webhook_secret_masked
                    ? "•••••••• (inchangé)"
                    : "Saisir le Webhook Secret"
                }
                autoComplete="new-password"
              />
            </Form.Item>
          </Col>
        </Row>

        <Title level={5}>Routage vers GestionVentes et la trésorerie</Title>
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Mode de paiement ELSA"
              name="payment_mode_id"
              extra="Facultatif : laissez vide pour créer automatiquement le mode canonique YengaPay."
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Créer automatiquement YengaPay"
                options={(config?.options?.payment_modes || []).map((mode) => ({
                  value: mode.id,
                  label: `${mode.name} (${mode.mode_type})`,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Compte de trésorerie destinataire"
              name="treasury_account_id"
              rules={[
                {
                  required: Boolean(enabled),
                  message: "Sélectionnez le compte qui reçoit les règlements.",
                },
              ]}
              extra="Le compte doit être actif, commercial, rattaché à l'établissement et dans la devise de l'entreprise."
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Sélectionner le compte destinataire"
                options={(config?.options?.treasury_accounts || []).map(
                  (account) => ({
                    value: account.id,
                    label: `${account.name} · ${
                      accountTypeLabels[account.account_type] ||
                      account.account_type
                    }${
                      account.currency_code ? ` · ${account.currency_code}` : ""
                    }${account.is_default ? " · Par défaut" : ""}`,
                  })
                )}
              />
            </Form.Item>
          </Col>
        </Row>

        {!config?.options?.treasury_accounts?.length && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Aucun compte de trésorerie commercial éligible"
            description="Créez d'abord un compte actif pour l'établissement dans Gestion Commerciale > Trésorerie."
          />
        )}

        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          message="URL du webhook à configurer dans la console YengaPay"
          description={
            <Space direction="vertical" style={{ width: "100%" }} size={4}>
              <Paragraph code style={{ margin: 0, wordBreak: "break-all" }}>
                {config?.webhook_url || "—"}
              </Paragraph>
              <Tooltip title="Copier l'URL">
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={copyWebhookUrl}
                  disabled={!config?.webhook_url}
                >
                  Copier l'URL du webhook
                </Button>
              </Tooltip>
            </Space>
          }
        />

        <Button
          data-testid="storefront-payment-save"
          type="primary"
          icon={<SaveOutlined />}
          size={isMobile ? "large" : "middle"}
          loading={saving}
          disabled={!canEditStores() || !config || !config.server_ready || conflict || Boolean(resource.error) || !dirty}
          onClick={handleSave}
        >
          Enregistrer la configuration de paiement
        </Button>
        <p className="storefront-help">{!canEditStores() ? "La modification de la configuration des boutiques est requise pour enregistrer le paiement."
          : conflict ? "Rechargez la configuration modifiée avant de poursuivre."
          : !config ? "Chargement de la configuration de paiement."
          : !config.server_ready ? "Le chiffrement serveur doit être configuré avant l'enregistrement."
          : !dirty ? "Aucune modification du paiement en attente." : "Le paiement s'enregistre séparément des paramètres de la vitrine."}</p>
      </Form>
    </Card>
  );
};

export default YengaPayConfigCard;
